import { create } from 'zustand';
import { IApplicationProps } from '../types/IApplicationProps';
import { appStateStorage, stateInitializer } from '../domain/services';
import { IBankDto } from '../domain/dto/IBankDto';
import { IProviderDto } from '../domain/dto/IProviderDto';
import ErrorScreenTypeEnum from '../types/enums/errorScreenTypeEnum';

interface IAppState {
  appState: IApplicationProps;
  setAppState: (newState: Partial<IAppState['appState']>) => void;
}
interface ISelectedProvider {
  selectedProvider?: IProviderDto;
  setSelectedProvider: (newState: IProviderDto) => void;
}
interface ISelectedBank {
  selectedBank?: IBankDto;
  setSelectedBank: (newState: IBankDto) => void;
}

interface IBankSearchState {
  bankSearch: string;
  selectedCountry: string;
  setBankSearch: (newState: string) => void;
  setSelectedCountry: (newState: string) => void;
}

interface IErrorState {
  error?: {
    type?: ErrorScreenTypeEnum;
    code?: string;
  };
  setError: (newState?: IErrorState['error']) => void;
}
export type FlowType = keyof typeof connectFlows;
interface IStepsState {
  steps: {
    title?: string;
    stepIndex: number;
    description?: string;
    singleStep?: boolean;
    infoBox?: {
      text: string;
      link?: {
        text: string;
        href: string;
        icon?: string;
      };
    };
  }[];
  activeStep: IStepsState['steps'][0] | null;
  setActiveStep: (newState: IStepsState['steps'][0]) => void;
  setFlow: (newFlow: FlowType) => void;
}

export const useSelectedProvider = create<ISelectedProvider>(set => ({
  selectedProvider: undefined,
  setSelectedProvider: selectedProvider => set(() => ({ selectedProvider }))
}));

export const useSelectedBank = create<ISelectedBank>(set => ({
  selectedBank: undefined,
  setSelectedBank: selectedBank => set(() => ({ selectedBank }))
}));

export const useAppState = create<IAppState>(set => ({
  appState: stateInitializer.init(),
  setAppState: newState =>
    set(state => {
      const stateToSet = { appState: { ...state.appState, ...newState } };
      appStateStorage.setDisableCreditInformationFlow(stateToSet.appState.disableCreditInformationFlow);
      return stateToSet;
    })
}));

export const useBankSearchState = create<IBankSearchState>(set => ({
  bankSearch: '',
  selectedCountry: '',
  setBankSearch: newState => set({ bankSearch: newState }),
  setSelectedCountry: selectedCountry => set({ selectedCountry })
}));

export const useErrorState = create<IErrorState>(set => ({
  error: {
    type: ErrorScreenTypeEnum.GenericError
  },
  setError: newState => set(state => ({ error: { ...state.error, ...newState } }))
}));

const connectFlows = {
  OpenBankingFlow: [
    {
      title: 'Step 1',
      stepIndex: 0,
      description:
        'Tell us who you bank with, so that we can connect to your accounts. You can connect as many banks and accounts as you need.'
    },
    {
      title: 'Step 2',
      stepIndex: 1,
      description: `Provide us with access to read your banking information, we’ll use this information to share with our trusted partners.`
    },
    {
      title: 'Step 3',
      stepIndex: 2,
      description: `Your account is successfully connected.`
    }
  ],
  CreditInformationFlow: [
    {
      title: 'Step 1',
      stepIndex: 0,
      description: 'Confirm your identity and access your credit report information held by TransUnion.'
    },
    {
      title: 'Step 2',
      stepIndex: 1,
      description: `Once we have your financial records, you can connect as many banks and accounts as you need.`
    },
    {
      title: 'Step 3',
      stepIndex: 2,
      description: `Your account is successfully connected.`
    }
  ],
  ReauthenticationFlow: [
    {
      title: 'Step 1',
      stepIndex: 0,
      description: 'Tell us if you choose to re-authenticate your account connection.'
    },
    {
      title: 'Step 2',
      stepIndex: 1,
      description: `Provide us with access to read your banking information, we’ll use this information to share with our trusted partners.`
    },
    {
      title: 'Step 3',
      stepIndex: 2,
      description: `Your account is successfully connected.`
    }
  ],
  ReconfirmationFlow: [
    {
      title: 'Step 1',
      stepIndex: 0,
      description: 'Tell us if you choose to re-confirm your account connection.'
    },
    {
      title: 'Step 2',
      stepIndex: 1,
      description: `Provide us with access to read your banking information, we’ll use this information to share with our trusted partners.`
    },
    {
      title: 'Step 3',
      stepIndex: 2,
      description: `Your account is successfully connected.`
    }
  ],
  DocumentUploadFlow: [
    {
      title: 'Step 1',
      stepIndex: 0,
      description:
        'Open a new tab and sign into your  online bank account. Export your bank statement in a .PDF format, maximum file size of 10mb.'
    },
    {
      title: 'Step 2',
      stepIndex: 1,
      description: `Upload your bank statement.`
    },
    {
      title: 'Step 3',
      stepIndex: 2,
      description: `Your upload is complete.`
    }
  ]
};

export enum CreditAccountsFetchStatus {
  Idle,
  Connecting,
  Waiting,
  Finished,
  Error
}

interface ICreditAccountsFetchStatus {
  state: {
    isTuConnectionSuccess?: boolean;
    creditAccountsFetchStatus: CreditAccountsFetchStatus;
  };
  setState: (params: Partial<ICreditAccountsFetchStatus['state']>) => void;
}

export const useCreditAccountsState = create<ICreditAccountsFetchStatus>(set => ({
  state: { creditAccountsFetchStatus: CreditAccountsFetchStatus.Idle, isTuConnectionSuccess: false },
  setState: newState => {
    return set(prev => ({ ...prev, state: { ...prev.state, ...newState } }));
  }
}));

export const useStepsState = create<IStepsState>(set => ({
  steps: [],
  activeStep: null,
  setActiveStep: newState =>
    set(state => ({
      ...state,
      activeStep: state.steps.find(s => s.stepIndex === newState.stepIndex)
    })),
  setFlow: newFlow =>
    set(state => ({
      ...state,
      steps: connectFlows[newFlow],
      activeStep: connectFlows[newFlow][0]
    }))
}));
